import type { CVRParticipant, GraphEdge, GraphNode, PersonDetails } from '../../../types'
import {
  detectCountry,
  formatDate,
  formatPercentRange,
  formatRoleLabel,
} from '../../../utils/format'
import { roleBadgeLabel } from '../../../utils/participantRole'
import { computeRootRelation } from './nodeRelationToRoot'

/** Title-case ALL-CAPS CVR role strings; leave mixed-case labels unchanged. */
function formatRoleDisplay(role: CVRParticipant['role']): string | null {
  const label = roleBadgeLabel(role)
  if (!label) return null
  const raw = role.function?.trim() || role.organizationType?.trim() || ''
  if (raw && raw === raw.toUpperCase() && /[A-ZÆØÅ]/.test(raw)) {
    return formatRoleLabel(raw)
  }
  return label
}

export interface GraphNodePanelData {
  countryCode: string | null
  beneficialStake: string | null
  roles: string[]
  involvedSince: string | null
  activeCompanyCount: number | null
  /** Formatted legal/beneficial stakes in root (e.g. "25 %"). */
  rootOwnershipStakes: string[]
  subsidiaryCount: number | null
  foundedDate: string | null
  /** Active beneficial owners of the selected company (names + optional %). */
  companyBeneficialOwners: string[]
}

export function participantMatchesNode(
  participant: CVRParticipant,
  node: GraphNode,
): boolean {
  if (node.enhedsNummer != null && participant.enhedsNummer === node.enhedsNummer) {
    return true
  }
  if (node.cvrNummer && participant.cvrNummer) {
    const pCvr = String(participant.cvrNummer).replace(/\D/g, '')
    if (pCvr.length === 8 && pCvr === node.cvrNummer) return true
  }
  return false
}

export function isDirectOwnerOfRoot(
  nodeId: string,
  rootId: string,
  edges: GraphEdge[],
): boolean {
  return edges.some(
    e => e.kind === 'legal' && e.from === nodeId && e.to === rootId,
  )
}

export function formatBeneficialOwnerSummaries(owners: CVRParticipant[]): string[] {
  return owners
    .filter(p => p.role.isActive)
    .map(p => {
      const pct = formatPercentRange(
        p.role.ownershipCapitalPercent,
        p.role.ownershipCapitalPercentMax,
      )
      const name = p.navn?.trim() || '—'
      return pct ? `${name} (${pct})` : name
    })
}

function earliestValidFrom(participants: CVRParticipant[]): string | null {
  let best: string | null = null
  for (const p of participants) {
    const from = p.role.validFrom
    if (!from) continue
    if (!best || from < best) best = from
  }
  return best
}

function resolveCountryCode(
  node: GraphNode,
  matches: CVRParticipant[],
  personDetails: PersonDetails | null,
): string | null {
  const addressParticipant = matches.find(p => p.adresse)
  const detected = detectCountry(
    addressParticipant?.adresse ?? personDetails?.adresse ?? undefined,
  )
  if (detected.countryCode) return detected.countryCode
  if (node.countryCode) return node.countryCode
  if (node.kind === 'company' && node.cvrNummer) return 'DK'
  return null
}

function formatRootOwnershipStakes(
  nodeId: string,
  rootId: string,
  edges: GraphEdge[],
): string[] {
  const relation = computeRootRelation(nodeId, rootId, [], edges)
  return relation.lines
    .filter(l => l.direction === 'owns-root')
    .map(l => {
      const pct = formatPercentRange(l.capitalMin, l.capitalMax)
      if (!pct) return null
      const via = l.pathCount > 1 ? ` (via ${l.pathCount} stier)` : ''
      if (l.kind === 'beneficial') return `${pct} (reel ejer)${via}`
      return `${pct}${via}`
    })
    .filter((s): s is string => !!s)
}

export function buildGraphNodePanelData(
  node: GraphNode,
  rootId: string,
  edges: GraphEdge[],
  allParticipants: CVRParticipant[],
  beneficialOwners: CVRParticipant[],
  personDetails: PersonDetails | null,
  companyStartdato: string | null | undefined,
  companySubsidiaryCount: number | null | undefined,
  companyBeneficialOwners: CVRParticipant[] = [],
): GraphNodePanelData {
  const matches = allParticipants.filter(p => participantMatchesNode(p, node))
  const countryCode = resolveCountryCode(node, matches, personDetails)
  const isCompany = node.kind === 'company' || node.kind === 'foreign-company'

  const beneficialMatch = beneficialOwners.find(
    p => participantMatchesNode(p, node) && p.role.isActive,
  )
  const beneficialStake = beneficialMatch
    ? formatPercentRange(
        beneficialMatch.role.ownershipCapitalPercent,
        beneficialMatch.role.ownershipCapitalPercentMax,
      )
    : null

  const roles = [
    ...new Set(
      matches
        .filter(p => p.role.isActive)
        .map(p => formatRoleDisplay(p.role))
        .filter((r): r is string => !!r),
    ),
  ]

  const involvedSince = formatDate(earliestValidFrom(matches))

  let activeCompanyCount: number | null = null
  if (node.kind === 'person' && personDetails) {
    activeCompanyCount = personDetails.affiliations.filter(a =>
      a.roles.some(r => r.isActive),
    ).length
  }

  const rootOwnershipStakes = !node.isRoot
    ? formatRootOwnershipStakes(node.id, rootId, edges)
    : []

  const subsidiaryCount = isCompany && !node.isRoot
    ? (companySubsidiaryCount ?? null)
    : null

  const foundedDate = isCompany && node.cvrNummer
    ? formatDate(companyStartdato ?? null)
    : null

  return {
    countryCode,
    beneficialStake,
    roles,
    involvedSince,
    activeCompanyCount,
    rootOwnershipStakes,
    subsidiaryCount,
    foundedDate,
    companyBeneficialOwners: isCompany && !node.isRoot
      ? formatBeneficialOwnerSummaries(companyBeneficialOwners)
      : [],
  }
}
